import React, { useEffect, useMemo, useState } from 'react';
import type { LeadDetail, ScheduleConsultationRequest } from '../api/intakeCrmApi';

interface ScheduleConsultationModalProps {
  lead: LeadDetail;
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: ScheduleConsultationRequest) => Promise<void>;
}

type MeetingMode = NonNullable<ScheduleConsultationRequest['meeting_mode']>;

function getDefaultStartTime(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 30);
  now.setSeconds(0, 0);
  const minutes = now.getMinutes();
  const roundedMinutes = minutes <= 30 ? 30 : 0;
  if (roundedMinutes === 0) {
    now.setHours(now.getHours() + 1);
  }
  now.setMinutes(roundedMinutes);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

const DURATION_OPTIONS = [30, 45, 60, 90, 120];

const ScheduleConsultationModal: React.FC<ScheduleConsultationModalProps> = ({
  lead,
  open,
  onClose,
  onSubmit,
}) => {
  const [startTime, setStartTime] = useState(getDefaultStartTime());
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [meetingMode, setMeetingMode] = useState<MeetingMode>('video');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [includeClientAsAttendee, setIncludeClientAsAttendee] = useState<boolean>(!!lead.email);
  const [createPrepTask, setCreatePrepTask] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStartTime(getDefaultStartTime());
    setDurationMinutes(60);
    setMeetingMode('video');
    setLocation('');
    setNotes('');
    setIncludeClientAsAttendee(!!lead.email);
    setCreatePrepTask(true);
    setError(null);
  }, [open, lead.email]);

  const attorneyName = lead.assignedToName || 'Assigned attorney';
  const practiceAreas = lead.leadProfile?.practiceAreas?.length
    ? lead.leadProfile.practiceAreas
    : (lead.practiceArea || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
  const jurisdiction = lead.leadProfile?.jurisdictionCourtName || lead.leadProfile?.jurisdiction || 'Not set yet';
  const summaryText = lead.leadProfile?.shortSummary || lead.intakeNotes || '';

  const startTimeDisplay = useMemo(() => {
    if (!startTime) return null;
    const parsed = new Date(startTime);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
  }, [startTime]);

  const handleSubmit = async () => {
    if (!startTime) {
      setError('Start time is required.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const start = new Date(startTime);
      const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
      await onSubmit({
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        duration_minutes: durationMinutes,
        meeting_mode: meetingMode,
        location: location.trim() || undefined,
        notes: notes.trim() || undefined,
        include_client_as_attendee: includeClientAsAttendee,
        create_prep_task: createPrepTask,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule consultation');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-700">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-sky-300">
              Consultation Scheduled
            </p>
            <h3 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
              Schedule Initial Consultation
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              This will place a client-linked consultation on {attorneyName}&rsquo;s calendar and optionally create a prep task.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid gap-6 px-6 py-5 lg:grid-cols-[1.25fr_0.95fr]">
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Start Time
                </span>
                <input
                  type="datetime-local"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900/40"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Duration
                </span>
                <select
                  value={durationMinutes}
                  onChange={(event) => setDurationMinutes(Number(event.target.value))}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900/40"
                >
                  {DURATION_OPTIONS.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {minutes} minutes
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Meeting Mode
                </span>
                <select
                  value={meetingMode}
                  onChange={(event) => setMeetingMode(event.target.value as MeetingMode)}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900/40"
                >
                  <option value="video">Video consultation</option>
                  <option value="phone">Phone consultation</option>
                  <option value="in_person">In-person consultation</option>
                  <option value="court">Court or courthouse consult</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Location / Meeting Detail
                </span>
                <input
                  type="text"
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                  placeholder={meetingMode === 'video' ? 'Zoom / Teams / Google Meet link' : meetingMode === 'phone' ? 'Dial-in or phone number' : 'Office, courtroom, or address'}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900/40"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Intake Notes For Attorney
              </span>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={5}
                placeholder="Anything the attorney should be briefed on before the consultation."
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900/40"
              />
            </label>

            <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/60">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={includeClientAsAttendee}
                  onChange={(event) => setIncludeClientAsAttendee(event.target.checked)}
                  className="mt-1 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900 dark:text-white">
                    Invite client as attendee
                  </span>
                  <span className="block text-xs text-slate-600 dark:text-slate-400">
                    {lead.email ? `Use ${lead.email} as the consultation invitee.` : 'No client email is available on this lead.'}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={createPrepTask}
                  onChange={(event) => setCreatePrepTask(event.target.checked)}
                  className="mt-1 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900 dark:text-white">
                    Create attorney prep task
                  </span>
                  <span className="block text-xs text-slate-600 dark:text-slate-400">
                    Mirrors the consultation onto EdgeTask so the attorney has a prep item linked to this lead.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-900/60 dark:bg-sky-950/30">
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                Calendar Target
              </p>
              <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                {attorneyName}
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                The event will be created on the assigned attorney&rsquo;s default calendar and labeled as an intake consultation.
              </p>
              {startTimeDisplay && (
                <p className="mt-3 text-sm font-medium text-sky-800 dark:text-sky-200">
                  {startTimeDisplay}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Lead Snapshot
              </p>
              <div className="mt-3 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{lead.clientName}</p>
                  <p>{lead.email || 'No email on file'}{lead.phone ? ` · ${lead.phone}` : ''}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Practice Areas</p>
                  <p>{practiceAreas.length > 0 ? practiceAreas.join(', ') : 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Jurisdiction</p>
                  <p>{jurisdiction}</p>
                </div>
                {summaryText && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Matter Summary</p>
                    <p className="line-clamp-5">{summaryText}</p>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Default reminders will follow the attorney calendar settings, with intake-safe fallbacks if none are configured.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:opacity-60"
            >
              {saving ? 'Scheduling...' : 'Schedule Consultation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleConsultationModal;
